/**
 * Error Messages Constants
 * Centralizes all error messages for consistent error handling across the app
 */

export enum ERROR_MESSAGES {
  // Auth Errors
  AUTH_INVALID_CREDENTIALS = 'Credenciales inválidas. Verifica tu email y contraseña.',
  AUTH_USER_NOT_FOUND = 'Usuario no encontrado. Por favor, regístrate primero.',
  AUTH_EMAIL_IN_USE = 'Este email ya está registrado. Intenta iniciar sesión.',
  AUTH_WEAK_PASSWORD = 'La contraseña es demasiado débil. Usa al menos 6 caracteres.',
  AUTH_SESSION_EXPIRED = 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.',
  AUTH_PERMISSION_DENIED = 'No tienes permisos para realizar esta acción.',
  AUTH_LOGIN_FAILED = 'Error al iniciar sesión. Intenta nuevamente.',
  AUTH_REGISTER_FAILED = 'Error al crear la cuenta. Intenta nuevamente.',
  AUTH_LOGOUT_FAILED = 'Error al cerrar sesión.',
  AUTH_RESET_PASSWORD_FAILED = 'Error al enviar el email de recuperación.',
  AUTH_UPDATE_PROFILE_FAILED = 'Error al actualizar el perfil.',
  AUTH_LOAD_PROFILE_FAILED = 'Error al cargar el perfil de usuario.',
  AUTH_SYNC_PROFILE_FAILED = 'Error al sincronizar el perfil.',
  AUTH_GOOGLE_LOGIN_FAILED = 'Error al iniciar sesión con Google.',

  // Network Errors
  NETWORK_OFFLINE = 'Sin conexión a internet. Verifica tu conexión.',
  NETWORK_TIMEOUT = 'La conexión tardó demasiado. Intenta nuevamente.',
  NETWORK_SERVER_ERROR = 'Error en el servidor. Intenta más tarde.',
  NETWORK_REQUEST_FAILED = 'Error al conectar con el servidor.',
  NETWORK_UNKNOWN_ERROR = 'Error de red desconocido. Intenta nuevamente.',

  // Payment Errors
  PAYMENT_PROCESSING_FAILED = 'Error al procesar el pago. Intenta nuevamente.',
  PAYMENT_CARD_DECLINED = 'La tarjeta fue rechazada. Intenta con otra.',
  PAYMENT_INSUFFICIENT_FUNDS = 'Fondos insuficientes. Intenta con otro método de pago.',
  PAYMENT_INVALID_CARD = 'Los datos de la tarjeta son inválidos.',
  PAYMENT_METHOD_NOT_SUPPORTED = 'Método de pago no soportado.',
  PAYMENT_CANCELLED = 'El pago fue cancelado.',
  PAYMENT_WALLET_INSUFFICIENT = 'Saldo insuficiente en tu billetera.',
  PAYMENT_VALIDATION_FAILED = 'Error al validar el pago.',
  PAYMENT_REJECTION_FAILED = 'Error al rechazar el pago.',

  // Cart & Checkout Errors
  CART_LOAD_FAILED = 'Error al cargar el carrito.',
  CART_UPDATE_FAILED = 'Error al actualizar el carrito.',
  CART_ITEM_NOT_FOUND = 'Producto no encontrado en el carrito.',
  CART_STOCK_UNAVAILABLE = 'No hay suficiente stock disponible.',
  CART_INVALID_QUANTITY = 'Cantidad inválida.',
  CHECKOUT_PROCESSING_FAILED = 'Error al procesar el pedido. Intenta nuevamente.',
  CHECKOUT_VALIDATION_FAILED = 'Error en los datos del pedido. Verifica la información.',
  SHIPPING_CALCULATION_FAILED = 'Error al calcular el envío.',

  // Product Errors
  PRODUCT_LOAD_FAILED = 'Error al cargar el producto.',
  PRODUCT_NOT_FOUND = 'Producto no encontrado.',
  PRODUCT_CREATE_FAILED = 'Error al crear el producto.',
  PRODUCT_UPDATE_FAILED = 'Error al actualizar el producto.',
  PRODUCT_DELETE_FAILED = 'Error al eliminar el producto.',
  PRODUCT_IMAGE_UPLOAD_FAILED = 'Error al subir la imagen.',
  PRODUCT_STOCK_UPDATE_FAILED = 'Error al actualizar el stock.',
  PRODUCT_CATEGORY_LOAD_FAILED = 'Error al cargar categorías.',

  // Shop Errors
  SHOP_LOAD_FAILED = 'Error al cargar la tienda.',
  SHOP_NOT_FOUND = 'Tienda no encontrada.',
  SHOP_CREATE_FAILED = 'Error al crear la tienda.',
  SHOP_UPDATE_FAILED = 'Error al actualizar la tienda.',
  SHOP_APPROVAL_FAILED = 'Error al aprobar la tienda.',
  SHOP_REJECTION_FAILED = 'Error al rechazar la tienda.',
  SHOP_VERIFICATION_FAILED = 'Error al verificar la tienda.',

  // Partner Errors
  PARTNER_LOAD_FAILED = 'Error al cargar el partner.',
  PARTNER_REGISTER_FAILED = 'Error al registrar el vendedor.',
  PARTNER_UPDATE_FAILED = 'Error al actualizar el partner.',
  PARTNER_APPROVAL_FAILED = 'Error al aprobar el partner.',
  PARTNER_SUSPENSION_FAILED = 'Error al suspender el partner.',
  PARTNER_PLAN_CHANGE_FAILED = 'Error al cambiar el plan.',

  // User Errors
  USER_LOAD_FAILED = 'Error al cargar usuarios.',
  USER_NOT_FOUND = 'Usuario no encontrado.',
  USER_UPDATE_FAILED = 'Error al actualizar el usuario.',
  USER_DELETE_FAILED = 'Error al eliminar el usuario.',

  // Order Errors
  ORDER_LOAD_FAILED = 'Error al cargar los pedidos.',
  ORDER_NOT_FOUND = 'Pedido no encontrado.',
  ORDER_CREATE_FAILED = 'Error al crear el pedido.',
  ORDER_UPDATE_FAILED = 'Error al actualizar el pedido.',
  ORDER_CANCEL_FAILED = 'Error al cancelar el pedido.',
  ORDER_TRACKING_FAILED = 'Error al obtener el seguimiento.',

  // Review Errors
  REVIEW_LOAD_FAILED = 'Error al cargar las reseñas.',
  REVIEW_CREATE_FAILED = 'Error al crear la reseña.',
  REVIEW_APPROVAL_FAILED = 'Error al aprobar la reseña.',
  REVIEW_REJECTION_FAILED = 'Error al rechazar la reseña.',

  // Content Errors
  CONTENT_LOAD_FAILED = 'Error al cargar el contenido.',
  CONTENT_CREATE_FAILED = 'Error al crear el contenido.',
  CONTENT_UPDATE_FAILED = 'Error al actualizar el contenido.',
  CONTENT_DELETE_FAILED = 'Error al eliminar el contenido.',
  STORY_CREATE_FAILED = 'Error al crear la historia.',
  STORY_DELETE_FAILED = 'Error al eliminar la historia.',

  // Category Errors
  CATEGORY_LOAD_FAILED = 'Error al cargar las categorías.',
  CATEGORY_CREATE_FAILED = 'Error al crear la categoría.',
  CATEGORY_UPDATE_FAILED = 'Error al actualizar la categoría.',
  CATEGORY_DELETE_FAILED = 'Error al eliminar la categoría.',

  // Wallet Errors
  WALLET_LOAD_FAILED = 'Error al cargar la billetera.',
  WALLET_TOPUP_FAILED = 'Error al recargar la billetera.',
  WALLET_TRANSACTION_FAILED = 'Error en la transacción.',
  WALLET_INSUFFICIENT_BALANCE = 'Saldo insuficiente.',

  // Club Errors
  CLUB_BID_FAILED = 'Error al pujar.',
  CLUB_JOIN_FAILED = 'Error al unirse al club.',
  CLUB_REDEEM_FAILED = 'Error al canjear recompensa.',

  // Service Errors
  SERVICE_LOAD_FAILED = 'Error al cargar los servicios.',
  SERVICE_BOOKING_FAILED = 'Error al realizar la reserva.',
  SERVICE_BLOCK_TIME_FAILED = 'Error al bloquear el tiempo.',
  SERVICE_UPDATE_STATUS_FAILED = 'Error al actualizar el estado.',

  // Generic Errors
  GENERIC_ERROR = 'Algo salió mal. Intenta nuevamente.',
  GENERIC_LOAD_ERROR = 'Error al cargar los datos.',
  GENERIC_SAVE_ERROR = 'Error al guardar. Intenta nuevamente.',
  GENERIC_DELETE_ERROR = 'Error al eliminar.',
  GENERIC_UPDATE_ERROR = 'Error al actualizar.',
  GENERIC_VALIDATION_ERROR = 'Error de validación. Verifica los datos.',
  GENERIC_NOT_FOUND = 'Recurso no encontrado.',
  GENERIC_UNAUTHORIZED = 'No autorizado para esta acción.',
  GENERIC_FORBIDDEN = 'Acceso denegado.',
  GENERIC_SERVER_ERROR = 'Error interno del servidor.',
  GENERIC_UNKNOWN_ERROR = 'Error desconocido.',
}

// Error categories for grouping
export const AUTH_ERRORS = [
  ERROR_MESSAGES.AUTH_INVALID_CREDENTIALS,
  ERROR_MESSAGES.AUTH_USER_NOT_FOUND,
  ERROR_MESSAGES.AUTH_EMAIL_IN_USE,
  ERROR_MESSAGES.AUTH_WEAK_PASSWORD,
  ERROR_MESSAGES.AUTH_SESSION_EXPIRED,
  ERROR_MESSAGES.AUTH_PERMISSION_DENIED,
] as const;

export const NETWORK_ERRORS = [
  ERROR_MESSAGES.NETWORK_OFFLINE,
  ERROR_MESSAGES.NETWORK_TIMEOUT,
  ERROR_MESSAGES.NETWORK_SERVER_ERROR,
  ERROR_MESSAGES.NETWORK_REQUEST_FAILED,
] as const;

export const PAYMENT_ERRORS = [
  ERROR_MESSAGES.PAYMENT_PROCESSING_FAILED,
  ERROR_MESSAGES.PAYMENT_CARD_DECLINED,
  ERROR_MESSAGES.PAYMENT_INSUFFICIENT_FUNDS,
  ERROR_MESSAGES.PAYMENT_INVALID_CARD,
] as const;

// Helper function to get user-friendly error message
export function getErrorMessage(error: unknown, fallback?: string): string {
  if (typeof error === 'string') {
    return error;
  }
  
  if (error instanceof Error) {
    // Check if the error message matches any known error
    const errorValue = Object.values(ERROR_MESSAGES).find(
      msg => error.message.toLowerCase().includes(msg.toLowerCase())
    );
    return errorValue || error.message;
  }
  
  return fallback || ERROR_MESSAGES.GENERIC_ERROR;
}

// Helper to check if an error is an auth error
export function isAuthError(error: unknown): boolean {
  const message = getErrorMessage(error);
  return AUTH_ERRORS.some(authError => message.includes(authError));
}

// Helper to check if an error is a network error
export function isNetworkError(error: unknown): boolean {
  const message = getErrorMessage(error);
  return NETWORK_ERRORS.some(networkError => message.includes(networkError));
}

// Helper to check if an error is a payment error
export function isPaymentError(error: unknown): boolean {
  const message = getErrorMessage(error);
  return PAYMENT_ERRORS.some(paymentError => message.includes(paymentError));
}
